import { useCallback, useEffect, useRef } from "react";
import { useRecoilState } from "recoil";
import { ocTableState, TableStateData } from "oc/states";

export function useOcTableState<T extends TableStateData>(
  tableId: string,
  defaultValue: T
): [T, (value: Partial<T>) => void] {
  const [state, setState] = useRecoilState(ocTableState(tableId));
  const initializedRef = useRef(false);

  // Initialize with default value if state is empty
  useEffect(() => {
    if (!initializedRef.current && Object.keys(state).length === 0) {
      setState(defaultValue);
      initializedRef.current = true;
    }
  }, [defaultValue, setState, state]);

  // Merge setter (same behavior as useOcClientState for objects)
  const setStateWithMerge = useCallback(
    (newValue: Partial<T>) => {
      setState((prev) => ({
        ...prev,
        ...newValue,
      }));
    },
    [setState]
  );

  // Return merged state with defaults - cast to T since we merge with defaultValue
  const resultState = (Object.keys(state).length === 0 ? defaultValue : { ...defaultValue, ...state }) as T;

  return [resultState, setStateWithMerge];
}

export default useOcTableState;
